import React, { useMemo, useState } from "react";
import { tWithKeyAttr as t } from "../../../../i18nHelper";
import styles from "../DeviceConfigForm.module.css";
import { DeviceConfig, LoretaMode } from "../../../../services/types/device.types";
import { LORETA_MODES, LORETA_OPTIONS } from "../../../../utils/loretaOptions";
import { DEFAULT_DURATION_MINUTES } from "../../../../utils/deviceConfiguration";

interface LoretaPanelProps {
  config: DeviceConfig;
  onChange: (patch: Partial<DeviceConfig>) => void;
}

function LoretaPanel({ config, onChange }: LoretaPanelProps) {
  const [search, setSearch] = useState("");
  const mode = config.loretaMode;
  const selections = config.loretaSelections ?? [];

  const options = useMemo(() => {
    if (!mode) return [];
    const all = LORETA_OPTIONS[mode];
    const needle = search.trim().toLowerCase();
    return needle ? all.filter((o) => o.toLowerCase().includes(needle)) : all;
  }, [mode, search]);

  const handleModeChange = (next: LoretaMode) => {
    // Catalogs do not overlap, so switching type clears the previous picks.
    onChange({ loretaMode: next, loretaSelections: [] });
    setSearch("");
  };

  const toggleSelection = (option: string) => {
    onChange({
      loretaSelections: selections.includes(option)
        ? selections.filter((s) => s !== option)
        : [...selections, option],
    });
  };

  return (
    <>
      <div className={styles.field}>
        <label className={styles.label}>
          {t("LORETA_MODE")} <span className={styles.req}>*</span>
        </label>
        <div className={styles.modeCards}>
          {LORETA_MODES.map((m) => (
            <button
              key={m.value}
              type="button"
              className={`${styles.modeCard} ${mode === m.value ? styles.modeCard_active : ""}`}
              onClick={() => handleModeChange(m.value)}
            >
              {t(m.labelKey)}
            </button>
          ))}
        </div>
      </div>

      {mode && (
        <div className={styles.field}>
          <label className={styles.label}>
            {t("LORETA_SELECTIONS")} <span className={styles.req}>*</span>
            <span className={styles.counter}>
              {selections.length}/{LORETA_OPTIONS[mode].length}
            </span>
          </label>
          <input
            type="text"
            className={styles.input}
            placeholder={t("SEARCH")}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          <div className={styles.optionList}>
            {options.map((option) => (
              <label key={option} className={styles.optionRow}>
                <input
                  type="checkbox"
                  checked={selections.includes(option)}
                  onChange={() => toggleSelection(option)}
                />
                <span dir="ltr">{option}</span>
              </label>
            ))}
            {options.length === 0 && <p className={styles.hintText}>{t("NO_RESULTS")}</p>}
          </div>

          {selections.length > 0 && (
            <div className={styles.chipRow}>
              {selections.map((s) => (
                <button
                  key={s}
                  type="button"
                  className={styles.chip}
                  onClick={() => toggleSelection(s)}
                  title={t("REMOVE")}
                >
                  <span dir="ltr">{s}</span>
                  <span aria-hidden>×</span>
                </button>
              ))}
            </div>
          )}
        </div>
      )}

      <div className={styles.field}>
        <label className={styles.label}>
          {t("DEVICE_DURATION_MINUTES")} <span className={styles.req}>*</span>
        </label>
        <input
          type="number"
          min={1}
          className={styles.input}
          value={config.durationMinutes ?? DEFAULT_DURATION_MINUTES}
          onChange={(e) =>
            onChange({ durationMinutes: e.target.value ? Number(e.target.value) : undefined })
          }
        />
      </div>
    </>
  );
}

export default LoretaPanel;
